import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { EditOutlined, UserOutlined } from "@ant-design/icons";
import { mdiPencilOff, mdiTranslate } from "@mdi/js";
import Icon from "@mdi/react";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Typography,
} from "antd";
import { reaction } from "mobx";
import { observer } from "mobx-react-lite";

import { api } from "../api";
import { getCountryName } from "../api/countries";
import { ContactOwnerModal } from "../components/profile/ContactOwnerModal";
import { DeleteProfileModal } from "../components/profile/DeleteProfileModal";
import { GreetingModal } from "../components/profile/GreetingModal";
import { ProfileSocialAccounts } from "../components/profile/ProfileSocialAccounts";
import { INDUSTRY_VALUES, LOCALE_LABELS, ROLE_VALUES } from "../config";
import { userStore } from "../stores/userStore";
import { getGravatarUrl } from "../utils/gravatar";
import { usePageTitle } from "../utils/usePageTitle";
import { usePendingAction } from "../utils/usePendingAction";
import { isValidPhone } from "../utils/validatePhone";

import "./ProfilePage.css";

import type { ProfileData } from "../api/profile";

export const ProfilePage = observer(function ProfilePage() {
  const { t, i18n } = useTranslation();
  usePageTitle(t("profile.title"));
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const { pending: saving, run: runSave } = usePendingAction();
  const [saveAlert, setSaveAlert] = useState<"success" | "error" | null>(null);
  const [greetingOpen, setGreetingOpen] = useState(
    () => sessionStorage.getItem("justRegistered") === "true",
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [contactOwnerGuid, setContactOwnerGuid] = useState<string | null>(
    () => {
      const next = searchParams.get("next") ?? "";
      return next.startsWith("contact")
        ? next.slice("contact".length) || null
        : null;
    },
  );
  const avatarReadyAtMount = useRef(userStore.avatarReady);
  const [avatarVisible, setAvatarVisible] = useState(userStore.avatarReady);

  useEffect(() => {
    return reaction(
      () => userStore.profile?.email,
      (email) => {
        if (email) {
          getGravatarUrl(email).then((url) => {
            const img = new window.Image();
            img.src = url;
            userStore.setAvatarUrl(url);
            if (img.complete) {
              userStore.setAvatarReady(true);
            }
          });
        }
      },
      { fireImmediately: true },
    );
  }, []);

  useEffect(() => {
    return reaction(
      () => userStore.profile,
      (profile) => {
        if (profile) form.setFieldsValue(profile);
      },
      { fireImmediately: true },
    );
  }, [form]);

  const countryOptions = userStore.countries.map((c) => ({
    value: c.code,
    label: getCountryName(c, i18n.language),
  }));

  const industryOptions = INDUSTRY_VALUES.map((value) => ({
    value,
    label: t(`profile.industries.${value}`),
  }));

  const roleOptions = ROLE_VALUES.map((value) => ({
    value,
    label: t(`profile.roles.${value}`),
  }));

  const localeOptions = Object.entries(LOCALE_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  async function handleSave(values: Omit<ProfileData, "email">) {
    await runSave(async () => {
      setSaveAlert(null);
      try {
        const result = await api.profile.updateProfile(values);
        if (result.ok) {
          userStore.setProfile({
            ...result.data,
            integrations:
              result.data.integrations ?? userStore.profile?.integrations,
          });
          if (result.data.locale && result.data.locale !== i18n.language) {
            await userStore.reloadCountries(result.data.locale);
            await i18n.changeLanguage(result.data.locale);
            localStorage.setItem("lang", result.data.locale);
          }
          setSaveAlert("success");
        } else {
          if (result.fieldErrors?.username) {
            form.setFields([
              { name: "username", errors: [t("profile.usernameTaken")] },
            ]);
            form.scrollToField("username", {
              block: "center",
              behavior: "smooth",
            });
          } else {
            setSaveAlert("error");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        }
      } catch {
        setSaveAlert("error");
      }
    });
  }

  function handleDelete() {
    setDeleteModalOpen(true);
  }

  if (userStore.loading) {
    return (
      <div className="profile-page__loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <GreetingModal
        open={greetingOpen}
        onClose={() => {
          sessionStorage.removeItem("justRegistered");
          setGreetingOpen(false);
        }}
      />
      <DeleteProfileModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
      />
      {contactOwnerGuid && (
        <ContactOwnerModal
          open={true}
          onClose={() => {
            setContactOwnerGuid(null);
            setSearchParams({}, { replace: true });
          }}
          webgisGuid={contactOwnerGuid}
        />
      )}
      <div className="page profile-page">
        <h1>{t("profile.title")}</h1>

        <h3>{t("profile.personalInfo")}</h3>

        <Row gutter={32} onClick={() => setSaveAlert(null)}>
          <Col xs={24} md={16}>
            {/* Email shown read-only outside the form to exclude from submission */}
            <div className="profile-page__email-row">
              <Typography.Text className="profile-page__field-label">
                {t("profile.email")}
              </Typography.Text>
              <Input
                size="large"
                value={userStore.profile?.email}
                readOnly
                aria-label={t("profile.email")}
                suffix={<Icon color={"grey"} path={mdiPencilOff} size={0.75} />}
              />
            </div>

            <Form form={form} layout="vertical" onFinish={handleSave}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="first_name" label={t("profile.firstName")}>
                    <Input size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="last_name" label={t("profile.lastName")}>
                    <Input size="large" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="username"
                label={t("profile.username")}
                rules={[
                  { required: true, message: t("signup.details.required") },
                  {
                    pattern: /^[a-zA-Z][a-zA-Z0-9\-_.+]{2,}[a-zA-Z0-9]$/,
                    message: t("profile.usernameInvalid"),
                  },
                ]}
              >
                <Input size="large" />
              </Form.Item>

              <Form.Item
                name="phone"
                label={t("profile.phone")}
                rules={[
                  {
                    validator: (_, value) =>
                      !value || isValidPhone(value)
                        ? Promise.resolve()
                        : Promise.reject(t("signup.details.phoneInvalid")),
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder={t("profile.phonePlaceholder")}
                />
              </Form.Item>

              <Form.Item
                name="country"
                label={t("profile.country")}
                rules={[
                  { required: true, message: t("signup.details.required") },
                ]}
              >
                <Select
                  size="large"
                  className="select-search"
                  classNames={{ input: "select-search__input" }}
                  options={countryOptions}
                  showSearch={{ optionFilterProp: "label" }}
                />
              </Form.Item>

              <Form.Item name="company" label={t("profile.company")}>
                <Input size="large" />
              </Form.Item>

              <Form.Item name="industry" label={t("profile.industry")}>
                <Select
                  size="large"
                  className="select-search"
                  classNames={{ input: "select-search__input" }}
                  options={industryOptions}
                  allowClear
                  showSearch={{ optionFilterProp: "label" }}
                />
              </Form.Item>

              <Form.Item name="role" label={t("profile.role")}>
                <Select size="large" options={roleOptions} allowClear />
              </Form.Item>

              <Form.Item
                name="locale"
                label={
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                      justifyItems: "center",
                    }}
                  >
                    <Icon path={mdiTranslate} size={0.6} />
                    {t("profile.language")}
                  </span>
                }
                rules={[
                  { required: true, message: t("signup.details.required") },
                ]}
              >
                <Select size="large" options={localeOptions} />
              </Form.Item>

              {saveAlert && (
                <Alert
                  type={saveAlert}
                  title={t(
                    saveAlert === "success"
                      ? "profile.savedSuccess"
                      : "errors.shared.common_error",
                  )}
                  showIcon
                  closable={{ afterClose: () => setSaveAlert(null) }}
                  style={{ marginBottom: 16 }}
                  styles={
                    saveAlert === "success"
                      ? {
                          root: {
                            background:
                              "color-mix(in srgb, var(--ng-green-color) 12%, white)",
                            borderColor:
                              "color-mix(in srgb, var(--ng-green-color) 35%, white)",
                          },
                          icon: { color: "var(--ng-green-color)" },
                        }
                      : undefined
                  }
                />
              )}

              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={saving}
              >
                {t("profile.save")}
              </Button>
            </Form>
          </Col>

          <Col xs={24} md={8}>
            <div className="profile-page__aside">
              <Typography.Text className="profile-page__field-label">
                {t("profile.photo")}
              </Typography.Text>
              <div className="profile-page__avatar-wrap">
                <Avatar
                  shape="square"
                  size={160}
                  icon={<UserOutlined />}
                  className="profile-page__avatar"
                />
                {userStore.avatarUrl && (
                  <img
                    src={userStore.avatarUrl}
                    className={`profile-page__avatar-overlay${avatarVisible || userStore.avatarReady ? " profile-page__avatar-overlay--visible" : ""}`}
                    style={
                      avatarReadyAtMount.current
                        ? { transition: "none" }
                        : undefined
                    }
                    alt=""
                    onLoad={() => {
                      setAvatarVisible(true);
                      userStore.setAvatarReady(true);
                    }}
                  />
                )}
                <a
                  href="https://gravatar.com/emails/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="profile-page__avatar-edit"
                  title={t("profile.changePhoto")}
                >
                  <EditOutlined />
                </a>
              </div>
            </div>
          </Col>
        </Row>

        <ProfileSocialAccounts />

        <h3 className="profile-page__management-title">
          {t("profile.management")}
        </h3>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Card className="profile-page__management-card">
              <div>
                <h4>{t("profile.export.title")}</h4>
                <p>{t("profile.export.description")}</p>
              </div>
              <Button href="/export_profile">
                {t("profile.export.button")}
              </Button>
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card className="profile-page__management-card">
              <div>
                <h4>{t("profile.removal.title")}</h4>
                <p>{t("profile.removal.description")}</p>
              </div>
              <Button danger onClick={handleDelete}>
                {t("profile.removal.button")}
              </Button>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
});
